import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import api from "api/api";

//Props Validations
import PropTypes from "prop-types";

// import mui components
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

//import mui icon
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

//import assets
import productCategory from "../assets/icons/productCategory.png";
import competitorProduct from "../assets/icons/competitorProduct.png";
import country from "../assets/icons/country.png";
import deodorantFormat from "../assets/icons/deodorantFormat.png";
import studyType from "../assets/icons/studyType.png";
import studyReference from "../assets/icons/studyReference.png";
import unileverProduct from "../assets/icons/unileverProduct.png";
import year from "../assets/icons/year.png";
import brand from "../assets/icons/brand.png";
import documents from "../assets/icons/documents.png";
import blind from "../assets/icons/blind-Icon.png";
import cbp from "../assets/icons/cbp-Icon.png";

//import component
import ActionStandardTooltipData from "../components/common/ActionStandardTooltipData";
import FiltersHeaderLabel from "../components/common/FiltersHeaderLabel";
import Header from "../components/common/Header";
import Loader from "../components/common/Loader";
import StudiesFiltersComponent from "../components/studyPage/StudiesFiltersComponent";
import ActionStandardTabs from "../components/actionStandards/ActionStandardTabs";

import MainNavigation from "../components/common/MainNavigation";

import StudySelectionSummary from "../components/studyPage/StudySelectionSummary";
import SearchBar from "../components/studyPage/SearchBar";
import CustomHeader from "../components/studyPage/CustomHeader";

import ReactCountryFlag from "react-country-flag";

//redux
import { useSelector, useDispatch } from "react-redux";
import { loadSelectedStudiesState, loadProductTestCodeMapState, loadListedStudiesState, loadNavigationAvailabilityState, loadStudyFiltersFlagState } from "store/features/studyDataSlice";
import { resetConsumerFilterStoreState } from "store/features/consumerFilterSlice";
import { loadDataMappingDisplayLabelState, loadDataMappingBackendVariableMacroCategoryState, loadDataMappingBackendVariableVariableCategoryState } from "store/features/dataMappingSlice";
import { resetAnalysisResultsStoreState, loadWeightToggleState, loadDisableWeightToggleState, loadProductColorsState, loadLikertScaleDataState } from "store/features/analysisResultsSlice";
import { loadProductOrderState, loadDefaultProductOrderState, resetProductsInfoStore } from "store/features/productsInfoSlice";
import { resetCorrelationStore } from "store/features/correlationSlice";
import { resetClaimsAnalysisStore } from "store/features/claimsAnalysisSlice";
import { loadTagsState } from "store/features/claimsMappingSlice";
import { resetRespondentDashboardStoreState } from "store/features/respondentDashboardSlice";
import { resetAnalysisKpiStoreState } from "store/features/analysisKpiSlice";
import { resetAnalysisTypeSelectionStoreState } from "store/features/analysisTypeSelectionSlice";
import { showNotification } from "store/features/notificationSlice";

import GetIconHeader from "../utils/GetIconHeader";
import FullScreenConatiner from "../components/layouts/FullScreenConatiner";
import GenerateColors from "../utils/charts/GenerateColors";
import GetNavigationButtons from "../utils/GetNavigationButtons";

const NoMaxWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
        placement="right"
    />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: "none",
        background: "linear-gradient(90deg, rgba(191, 242, 255, 1) 0%, rgba(195, 215, 255, 1) 100%)",
        borderRadius: "5px",
        padding: "5px 0px",
    },
});

const ActTool = ({ style, data, studyType, studyTypeMethod, paramsData }) => {
    const defaultTitle = paramsData?.study_type_method__default_title ?? "";
    return (
        <NoMaxWidthTooltip
            title={
                <div>
                    <ActionStandardTooltipData
                        data={data}
                        onlyKpi={true}
                        studyType={studyType}
                        studyTypeMethod={studyTypeMethod}
                        defaultTitle={defaultTitle}
                    />
                </div>
            }
        >
            <div
                style={{
                    ...style,
                    marginBottom: "8px",
                }}
            ></div>
        </NoMaxWidthTooltip>
    );
};

// Typechecking props for the Study
ActTool.propTypes = {
    style: PropTypes.object,
    data: PropTypes.array,
    studyType: PropTypes.string,
    studyTypeMethod: PropTypes.string,
    paramsData: PropTypes.object,
};

const ovalStyleRed = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "red", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
};
const ovalStyleGreen = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "green", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
};
const ovalStyleWhite = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "white", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
    border: "1px solid black",
};

const ovalStyleYellow = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%",
    backgroundColor: "#FFBF00",
    marginRight: "10px",
    cursor: "pointer",
};

const parseResult = (result) => {
    if (!result) return {};
    try {
        return typeof result === "object" ? result : JSON.parse(result);
    } catch {
        return {};
    }
};

const getResultData = (datum) => {
    const grp = datum?.action_standard_groups;
    const parsed = parseResult(grp?.result);
    if (Object.keys(parsed).length > 0) return parsed;
    return parseResult(grp?.overall_result);
};
const getResultValues = (datum) => Object.values(getResultData(datum)).map((v) => v?.toLowerCase());
const ParityValue = (datum) => getResultValues(datum).some((v) => v === "parity");
const MetValue = (datum) => getResultValues(datum).some((v) => v === "met");
const NotMetValue = (datum) => getResultValues(datum).some((v) => v === "not met");

function Study({ headerTitle = "" }) {
    const dispatch = useDispatch();
    const gridRef = useRef();
    const navigate = useNavigate();
    const isInitialMount = useRef(true);

    const studyPageFilters = useSelector((state) => state.studyDataStore.studyPageFilters);
    const selectedStudies = useSelector((state) => state.studyDataStore.selectedStudies);
    // const navigationAvailability = useSelector((state) => state.studyDataStore.navigationAvailability);
    const filteredCoreDataIdsFromSearchFilters = useSelector((state) => state.studyDataStore.filteredCoreDataIdsFromSearchFilters);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const [disabledRows, setDisabledRows] = useState({});
    const studyTypeMethod = selectedStudies.length > 0 ? [...new Set(selectedStudies.map((ele) => ele.study_type_method))].join(",") : null;

    const [actionStandardData, setActionStandardData] = useState([]);
    const [textThemeAnalysisNote, setTextThemeAnalysisNote] = useState("");

    const [rowData, setRowData] = useState([]);
    const [rowsToListInGrid, setRowsToListInGrid] = useState([]);
    const [selectedRow, setSelectedRow] = useState([]);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [btnDisabled, setBtnDisabled] = useState({
        overallKpi: true,
        agreementKpi: true,
        claims: true,
        correlation: true,
        justRight: true,
    });

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    //loader variables
    const [isLoading, setIsLoading] = useState(false);
    const [navigationLoader, setNavigationLoader] = useState(false);
    const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(false);
    const role = useSelector((state) => state.userStore.role) || null;

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;
    const btnArr = GetNavigationButtons(selectedStudies, studyPageFilters, isOralCare || isSkinCleansing || isHairCare || isSkinCare, isUnileverFragranceHouse);
    const [isFragrance, setIsFragrance] = useState(false);
    const [actionStandardModal, setActionStandardModal] = useState(false);
    const [snackInfo, setSnackInfo] = useState(["error", ""]);
    const [snackBarOpen, setSnackBarOpen] = useState(false);

    const [selectedStudyTypeMethodIds, setSelectedStudyTypeMethodIds] = useState([]);
    const [checkStatus, setCheckStatus] = useState(false);
    const [stmTemplateMap, setStmTemplateMap] = useState({});
    const filterKey = useMemo(() => {
        if (!studyPageFilters) return "";
        return Object.keys(studyPageFilters)
            .sort()
            .map(key => {
                const checkedValues = (studyPageFilters[key] || [])
                    .filter(item => item.checked)
                    .map(item => item.value) 
                    .sort() 
                    .join(',');
                return `${key}=[${checkedValues}]`;
            })
            .join('&');
    }, [studyPageFilters]);

    useEffect(() => {
        const fetchStudyTypeMethods = async () => {
            try {
                const response = await api.get('/study-type-methods/');
                const templateMap = {};
                response.data.forEach(stm => {
                    templateMap[stm.id] = stm.default_title;
                });
                setStmTemplateMap(templateMap);
            } catch (error) {
                handleError("error", error);
            }
        };
        fetchStudyTypeMethods();
    }, []);

    const filterBasedSelectAll = useMemo(() => {
        const selectedMethods = studyPageFilters?.studyTypeMethod?.filter(e => e.checked) || [];

        if (selectedMethods.length === 0) {
            return { show: false, methodIds: [] };
        }
        if (selectedMethods.length === 1) {
            return { show: true, methodIds: selectedMethods.map(e => e.value) };
        }
        const templates = selectedMethods.map(method => stmTemplateMap[method.value]);
        const firstTemplate = templates[0];
        const allShareSameTemplate = templates.every(template => template === firstTemplate);
        if (allShareSameTemplate) {
            return { 
                show: true, 
                methodIds: selectedMethods.map(e => e.value) 
            };
        }
        return { show: false, methodIds: [] };
    }, [studyPageFilters, stmTemplateMap]);
    const shouldShowSelectAll = useMemo(() => {
        return filterBasedSelectAll.show || selectedRow.length > 0;
    }, [filterBasedSelectAll.show, selectedRow.length]);

    useEffect(() => {
        const selectedRows = gridRef.current?.api?.getSelectedRows() || [];
        const selectedRowCount = selectedRows.length;
        
        if (selectedRowCount === 0) {
            setCheckStatus(false);
            return;
        }
        
        let totalCompatibleRows = 0;
        const gridApi = gridRef.current?.api;
        let idsToCheck;
        if (filterBasedSelectAll.show && selectedRowCount > 0) {
            idsToCheck = filterBasedSelectAll.methodIds;
        } else if (selectedRowCount > 0) {
            idsToCheck = selectedStudyTypeMethodIds;
        } else {
            idsToCheck = [];
        }
        
        if (gridApi && idsToCheck.length > 0) {
            gridApi.forEachNode((node) => {
                if (idsToCheck.includes(node.data.study_type_method)) {
                    totalCompatibleRows++;
                }
            });
        }
        
        if (totalCompatibleRows > 0 && selectedRowCount === totalCompatibleRows) {
            setCheckStatus(true);
        } else {
            setCheckStatus(false);
        }
    }, [selectedRow, selectedStudyTypeMethodIds, filterBasedSelectAll]);

    useEffect(() => {
        if (studyPageFilters?.studyType?.length) {
            const checkIsFragranceByPageFilters = studyPageFilters?.studyType?.every((ele) => (ele.label.toLowerCase() === process.env?.REACT_APP_FRAGRANCE_TESTING?.toLowerCase() ? ele.checked : !ele.checked));
            if (!checkIsFragranceByPageFilters) {
                if (selectedStudies.length) {
                    const hasFragrance = selectedStudies.every((study) => study.study_type__title.toLowerCase() === process.env?.REACT_APP_FRAGRANCE_TESTING?.toLowerCase());
                    if (hasFragrance) {
                        // Disabled the fragrance testing analysis modules for prod release
                        setBtnDisabled({
                            fragranceMetricsAnalysis: false,
                            preferenceAnalysis: false,
                            fragranceAssociation: false,
                            agreementkpi: false,
                            productPreferenceAnalysis: false,
                        });
                        setIsFragrance(hasFragrance);
                    } else {
                        setIsFragrance(checkIsFragranceByPageFilters);
                    }
                } else {
                    setIsFragrance(false);
                }
            } else {
                setIsFragrance(checkIsFragranceByPageFilters);
            }
        }
    }, [studyPageFilters, selectedStudies]);

    useEffect(() => {
        const filteredIds = filteredCoreDataIdsFromSearchFilters?.map(Number) || null;
        if (filteredIds) {
            if (filteredIds.length) {
                const data = rowData.filter((datum) => filteredIds.includes(datum.id));
                setRowsToListInGrid([...data]);
                dispatch(loadListedStudiesState([...data]));
            } else {
                setRowsToListInGrid([]);
                dispatch(loadListedStudiesState([]));
            }
        } else {
            setRowsToListInGrid([...rowData]);
            dispatch(loadListedStudiesState([...rowData]));
        }
    }, [rowData, filteredCoreDataIdsFromSearchFilters, dispatch]);

    const handleStudyTypeHeaderSelection = (checked) => {
        if (!checked) {
            gridRef.current.api.deselectAll();
            setCheckStatus(false);
            return;
        }

        const selectedRows = gridRef.current.api.getSelectedRows();
        let idsToSelect;

        if (filterBasedSelectAll.show) {
            idsToSelect = filterBasedSelectAll.methodIds;
        } else if (selectedRows.length > 0) {
            idsToSelect = selectedStudyTypeMethodIds;
        }
        
        if (!idsToSelect || idsToSelect.length === 0) return;

        const nodesToSelect = [];
        gridRef.current.api.forEachNode((node) => {
            if (idsToSelect.includes(node.data.study_type_method)) {
                nodesToSelect.push(node);
            }
        });
        
        gridRef.current.api.setNodesSelected({
            nodes: nodesToSelect,
            newValue: true,
        });
        
        const totalRows = rowsToListInGrid.length;
        if (nodesToSelect.length === totalRows) {
            setCheckStatus(true);
        } else {
            setCheckStatus(false);
        }
    };

    const fetchLikertScaleData = useCallback(() => {
        try {
            let url = `${process.env.REACT_APP_SERVER_PATH}/data-mappings/likert-scale/?product_category=${productCategoryId}`;
            if (studyTypeMethod) {
                url += `&study_type_method=${studyTypeMethod}`;
            }
            api.get(url)
                .then((response) => {
                    dispatch(loadLikertScaleDataState(response.data));
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [productCategoryId, dispatch, studyTypeMethod]);

    useEffect(() => {
        if (productCategoryId) {
            fetchLikertScaleData();
        }
    }, [productCategoryId, fetchLikertScaleData, selectedStudies]);

    useEffect(() => {
        const products = selectedStudies.map((study) => [...study.products, ...study.competitor_products]).flat();
        const colors = GenerateColors(products.length);
        dispatch(loadProductColorsState(colors));
        if (selectedStudies.length > 0) {
            setIsCheckingCompatibility(true);
            let byAncestorSTMIds;
            const type = selectedStudies[0]?.study_type;
            const studyTypeMethodId = selectedStudies[0].study_type_method;
            const url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/by-ancestor/${studyTypeMethodId}/`;
            try {
                api.get(url)
                    .then(async (res) => {
                        byAncestorSTMIds = res.data.map((datum) => datum.id);
                        setSelectedStudyTypeMethodIds(byAncestorSTMIds);
                        const disabledMap = {};
                        rowData.forEach((row) => {
                            if (row.study_type === type && byAncestorSTMIds.includes(row.study_type_method)) {
                                disabledMap[row.id] = false; // Enable this row
                            } else {
                                disabledMap[row.id] = true; // Disable this row
                            }
                        });
                        setDisabledRows(disabledMap); // Update the disable state to disable/enable rows
                        setIsCheckingCompatibility(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                        setIsCheckingCompatibility(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsCheckingCompatibility(false);
            }
        } else if (selectedStudies.length === 0) {
            setDisabledRows({}); // update the disable state to default
            dispatch(loadNavigationAvailabilityState({})); // reset navigation availability state
        }
    }, [selectedStudies, dispatch, rowData]);

    // Reset disabled rows when filters change
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setDisabledRows({});
            setSelectedStudyTypeMethodIds([]);
            if (gridRef.current?.api) {
                gridRef.current.api.deselectAll();
                setSelectedRow([]);
                setCheckStatus(false);
            }
        }
    }, [filterKey]);

    useEffect(() => {
        async function fetchNavigation(coreDataIds) {
            setNavigationLoader(true);
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/core-data-models/navigation-availability/?core_data_ids=${coreDataIds}`;
                if (productCategoryId) {
                    url += `&product_category=${productCategoryId}`;
                }
                if (studyTypeMethod) {
                    url += `&study_type_method=${studyTypeMethod}`;
                }
                await api
                    .get(url)
                    .then((res) => {
                        dispatch(loadNavigationAvailabilityState(res.data));
                        showTextTheamNote(selectedStudies, res.data);
                        setNavigationLoader(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setNavigationLoader(false);
                    });
            } catch (error) {
                handleError("error", error);
                setNavigationLoader(false);
            }
        }

        if (selectedStudies.length > 0) {
            const coreDataIds = selectedStudies.map((ele) => ele.id).join(",");
            fetchNavigation(coreDataIds);
        }
    }, [selectedStudies, productCategoryId, studyTypeMethod, btnDisabled, dispatch]);

    const selectRows = useCallback(() => {
        const rowsToSelect = selectedStudies?.map((ele) => ele.id);
        const selected = [];
        gridRef?.current?.api.forEachNode(function (node) {
            if (node.data && rowsToSelect?.includes(node.data.id)) {
                selected.push(node);
            }
            gridRef?.current?.api.setNodesSelected({ nodes: selected, newValue: true });
        });
    }, [selectedStudies]);

    const getActionStandard = useCallback(() => {
        const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False`;
        try {
            api.get(url)
                .then(async (res) => {
                    setActionStandardData(res.data);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, []);

    useEffect(() => {
        getActionStandard();
    }, [getActionStandard]);

    useEffect(() => {
        const arr = [];
        selectedStudies.forEach((item) => {
            arr.push(item?.["weighted"]);
        });
        const flag = arr.some((ele) => ele === true);

        if (flag) {
            dispatch(loadWeightToggleState(flag));
            dispatch(loadDisableWeightToggleState(!flag));
        }

        const sorted = selectedStudies
            .map((datum) => {
                const pro = Array.from(datum.products)
                    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    .map((ele) => {
                        return {
                            productCode: ele,
                            coreDataId: datum.id,
                            isCompetitor: false,
                        };
                    });
                const ulPro = Array.from(datum.competitor_products)
                    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    .map((ele) => {
                        return {
                            productCode: ele,
                            coreDataId: datum.id,
                            isCompetitor: true,
                        };
                    });
                return [...pro, ...ulPro];
            })
            .flat();

        dispatch(loadProductOrderState(sorted));
        dispatch(loadDefaultProductOrderState(sorted));
    }, [selectedStudies, dispatch]);

    useEffect(() => {
        if (selectedStudies.length === 0) {
            setTextThemeAnalysisNote("");
        }
    }, [selectedStudies]);

    const showTextTheamNote = (selectedStudies, navigationAvailability) => {
        const areStudiesSelected = selectedStudies.length > 0;
        const isDeodorantsCategory = productCategoryData?.label?.toLowerCase() === "deodorants";
        const allStudiesAreHomeUseTest = areStudiesSelected && selectedStudies.every((study) => study?.study_type__title?.toLowerCase() === "home use test");

        if (isDeodorantsCategory && allStudiesAreHomeUseTest) {
            if (selectedStudies.length > 1) {
                setTextThemeAnalysisNote(
                    "Note: Please note that ‘Theme Text Analysis’ can be delivered only when a single study is selected"
                );
            }
            else if (selectedStudies.length === 1) {
                // if (navigationLoader === false) {
                    if (navigationAvailability && navigationAvailability.has_open_ended_variables === false) {
                        setTextThemeAnalysisNote(
                            "Note: The selected study does not contain any open-ended variables; therefore, ‘Theme Text Analysis’ is not available within the analysis modules"
                        );
                    } else {
                        setTextThemeAnalysisNote("");
                    }
                // }
            }
        } else {
            setTextThemeAnalysisNote("");
        }
    };

    const closeActionStandardModal = (type = "", errorMessage = "") => {
        if (type === "success") {
            setSnackInfo([type, "Action standards submitted for the study"]);
            setSnackBarOpen(true);
            setActionStandardModal(false);
        } else if (type === "close") {
            setActionStandardModal(false);
        } else {
            setSnackInfo(["error", JSON.stringify(errorMessage.response.data)]);
            setSnackBarOpen(true);
        }
    };
    const handleSnackBarClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackBarOpen(false);
    };
    const onFirstDataRendered = useCallback(() => {
        setTimeout(() => {
            selectRows();
        }, 1000);

        setTimeout(() => {
            Array.from(document.getElementsByClassName("ag-text-field-input")).forEach((obj) => {
                obj.setAttribute("placeholder", "Search");
                if (obj.attributes["disabled"]) {
                    return;
                }
            });

            Array.from(document.getElementsByClassName("ag-number-field-input")).forEach((obj) => {
                obj.setAttribute("placeholder", "Search");
                if (obj.attributes["disabled"]) {
                    return;
                }
            });
        }, 100);

        Array.from(document.getElementsByClassName("ag-text-field-input")).forEach((obj) => {
            obj.setAttribute("placeholder", "Search");
            if (obj.attributes["disabled"]) {
                return;
            }
        });

        Array.from(document.getElementsByClassName("ag-number-field-input")).forEach((obj) => {
            if (obj.attributes["disabled"]) {
                return;
            }
            obj.setAttribute("placeholder", "Search");
        });
    }, [selectRows]);

    const onBodyScroll = useCallback(() => {
        setTimeout(() => {
            Array.from(document.getElementsByClassName("ag-text-field-input")).forEach((obj) => {
                obj.setAttribute("placeholder", "Search");
                if (obj.attributes["disabled"]) {
                    return;
                }
            });

            Array.from(document.getElementsByClassName("ag-number-field-input")).forEach((obj) => {
                obj.setAttribute("placeholder", "Search");
                if (obj.attributes["disabled"]) {
                    return;
                }
            });
        }, 100);

        Array.from(document.getElementsByClassName("ag-text-field-input")).forEach((obj) => {
            obj.setAttribute("placeholder", "Search");
            if (obj.attributes["disabled"]) {
                return;
            }
        });

        Array.from(document.getElementsByClassName("ag-number-field-input")).forEach((obj) => {
            if (obj.attributes["disabled"]) {
                return;
            }
            obj.setAttribute("placeholder", "Search");
        });
    }, []);

    const fetchDisplayLabels = useCallback(async () => {
        try {
            let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/display-label-translation/?archive=false&uniq=true`;
            if (productCategoryId) {
                url += `&product_category=${productCategoryId}`;
            }
            if (studyTypeMethod) {
                url += `&study_type_method=${studyTypeMethod}`;
            }
            await api
                .get(url)
                .then((res) => {
                    dispatch(loadDataMappingDisplayLabelState(res.data.display_label_translation));
                    dispatch(loadDataMappingBackendVariableMacroCategoryState(res.data.backend_variable_macro_category_translation));
                    dispatch(loadDataMappingBackendVariableVariableCategoryState(res.data.backend_variable_variable_category_translation));
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [dispatch, productCategoryId, studyTypeMethod]);

    useEffect(() => {
        fetchDisplayLabels();
    }, [fetchDisplayLabels]);

    useEffect(() => {
        if (selectedStudies.length === 0) {
            setSelectedRow([]);
        }
    }, [selectedStudies]);

    const fetchOralCareTestCodeData = useCallback(() => {
        const coreDataIds = rowsToListInGrid.map((study) => study.id).join(",");
        const url = process.env.REACT_APP_SERVER_PATH + `/study-datas/product-code-test-code-map/?core_data_ids=${coreDataIds}`;
        try {
            api.get(url)
                .then(async (res) => {
                    dispatch(loadProductTestCodeMapState(res.data));
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [rowsToListInGrid, dispatch]);

    useEffect(() => {
        if (isOralCare) {
            fetchOralCareTestCodeData();
        }
    }, [fetchOralCareTestCodeData, isOralCare]);

    const linkCellRenderer = (params) => {
        return (
            <Link
                to={params.value}
                target="_blank"
            >
                <OpenInNewIcon />
            </Link>
        );
    };
    const multipleCellRenderer = (params) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
            return (
                <div className="ag-multi-line-cell-render">
                    {params.value.map((val, i) => {
                        return <p key={i + 1}>{val}</p>;
                    })}
                </div>
            );
        } else {
            return params.value;
        }
    };

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };
    const prodCol = [
        ...(isUnileverFragranceHouse
            ? [
                  {
                      headerName: "Business group",
                      field: "business_group",
                      checkboxSelection: (params) => !disabledRows[params.data.id],
                      sortable: true,
                      suppressMovable: false,
                      headerComponent: CustomHeader,
                      headerComponentParams: {
                          displayName: "Business Group",
                          icon: productCategory,
                          enableCheckbox: shouldShowSelectAll,
                          onHeaderCheckboxChanged: handleStudyTypeHeaderSelection,
                          checkStatus: checkStatus,
                      },
                  },
              ]
            : []),
        {
            headerName: "Product Category",
            field: "product_category__title",
            checkboxSelection: isUnileverFragranceHouse ? false : (params) => !disabledRows[params.data.id],
            // headerCheckboxSelection: () => selectedRow.length > 0,
            // headerCheckboxSelection: true,
            // headerCheckboxSelectionFilteredOnly: true,
            // headerComponent: HeaderCheckbox,
            // headerComponentParams: {
            //     template: GetIconHeader("Product Category", productCategory),
            // },
            sortable: true,
            suppressMovable: false,

            ...(!isUnileverFragranceHouse && {
                headerComponent: CustomHeader,
                headerComponentParams: {
                    displayName: "Product Category",
                    icon: productCategory,
                    enableCheckbox: shouldShowSelectAll,
                    onHeaderCheckboxChanged: handleStudyTypeHeaderSelection,
                    checkStatus: checkStatus,
                },
            }),
        },
        {
            headerName: "Study Type",
            field: "study_type__title",
            headerComponentParams: {
                template: GetIconHeader("Study Type", competitorProduct),
            },
        },
        ...[
            ...(isBeautyAndWellbeing
                ? [
                      {
                          headerName: isSkinCare ? "Blind/Branded" : "Blind/Branded/Full Mix",
                          field: "blind_branded",
                          headerComponentParams: {
                              template: GetIconHeader(isSkinCare ? "Blind/Branded" : "Blind/Branded/Full Mix", blind),
                          },
                      },
                  ]
                : []),
            ...(isHairCare
                ? [
                      {
                          headerName: "CBP",
                          field: "consumer_benefit_platform",
                          headerComponentParams: {
                              template: GetIconHeader("CBP", cbp),
                          },
                      },
                  ]
                : []),
        ],
        {
            headerName: "Study Type Method",
            field: "study_type_method__title",
            headerComponentParams: {
                template: GetIconHeader("Study Type Method", studyType),
            },
        },
        {
            headerName: "Country",
            field: "country__title",
            cellRenderer: (params) => {
                return (
                    <div>
                        <ReactCountryFlag
                            countryCode={params.data.country__description}
                            svg
                            style={{
                                width: "1.5em",
                                height: "1.5em",
                                marginRight: "10px",
                            }}
                        />
                        <span>{params.data.country__title}</span>
                    </div>
                );
            },
            headerComponentParams: {
                template: GetIconHeader("Country", country),
            },
        },
        {
            headerName: "Unilever Brand",
            field: "unilever_brands__titles",
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            wrapText: true,
            headerComponentParams: {
                template: GetIconHeader("Unilever Brand", brand),
            },
        },
        {
            headerName: " Study Period",
            field: "study_period__title",
            // sort: "desc",
            // comparator: dateComparator,
            headerComponentParams: {
                template: GetIconHeader("Study Period", year),
            },
            valueFormatter: function (params) {
                const updatedDateFormat = params.value.split("/");
                return updatedDateFormat[1] + "/" + updatedDateFormat[0]; // Date format changed to mm/yyyy
            },
        },
        {
            headerName: "Product Format",
            field: "format_data",
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            wrapText: true,
            headerComponentParams: {
                template: GetIconHeader("Product Format", deodorantFormat),
            },
        },
        ...(isSkinCare && role?.toLowerCase() === "user"
            ? []
            : [
                  {
                      headerName: "Action Standard",
                      field: "action_standard",
                      autoHeight: true,
                      wrapText: true,
                      filter: false,
                      sortable: false,
                      headerComponentParams: {
                          template: GetIconHeader("Action Standard", year),
                      },
                      cellRenderer: (params) => {
                          const actionStdCore = actionStandardData.filter((datum) => datum.action_standard_groups.core_data_model_id === params.data.id);
                          const isFragranceTesting = params.data.study_type__title.toLowerCase() === process.env.REACT_APP_FRAGRANCE_TESTING?.toLowerCase();
                          const defaultTitle = params.data.study_type_method__default_title;
                          const useParsedResultLogic = isUnileverFragranceHouse ? defaultTitle?.toLowerCase() === "clt" : isFragranceTesting;

                          let metData;
                          let notMetData;

                          if (useParsedResultLogic) {
                              metData = actionStdCore.filter((datum) => Object.values(JSON.parse(datum?.action_standard_groups?.result) || {})?.[0]?.toLowerCase() === "met") || {};
                              notMetData = actionStdCore.filter((datum) => Object.values(JSON.parse(datum?.action_standard_groups?.result) || {})?.[0]?.toLowerCase() !== "met") || {};
                          } else {
                              metData = actionStdCore.filter((datum) => datum?.action_standard_groups?.result?.toLowerCase() === "met") || {};
                              notMetData = actionStdCore.filter((datum) => datum?.action_standard_groups?.result?.toLowerCase() !== "met") || {};
                          }
                          if (isBeautyAndWellbeing) {
                              const parityData = actionStdCore.filter((datum) => ParityValue(datum));
                              const metData = actionStdCore.filter((datum) => MetValue(datum));
                              const notMetData = actionStdCore.filter((datum) => NotMetValue(datum));

                              if (metData.length === 0 && notMetData.length === 0 && parityData.length === 0) {
                                  return (
                                      <div
                                          style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                          }}
                                      >
                                          <div style={ovalStyleWhite}></div>
                                      </div>
                                  );
                              }
                              return (
                                  <div
                                      style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                      }}
                                  >
                                      {metData.length !== 0 && (
                                          <ActTool
                                              data={metData}
                                              style={ovalStyleGreen}
                                              studyType={params.data?.study_type__title}
                                              studyTypeMethod={params.data?.study_type_method__title}
                                              paramsData={params.data}
                                          />
                                      )}

                                      {parityData.length !== 0 && (
                                          <ActTool
                                              data={parityData}
                                              style={ovalStyleYellow}
                                              studyType={params.data?.study_type__title}
                                              studyTypeMethod={params.data?.study_type_method__title}
                                              paramsData={params.data}
                                          />
                                      )}
                                      {notMetData.length !== 0 && (
                                          <ActTool
                                              data={notMetData}
                                              style={ovalStyleRed}
                                              studyType={params.data?.study_type__title}
                                              studyTypeMethod={params.data?.study_type_method__title}
                                              paramsData={params.data}
                                          />
                                      )}
                                  </div>
                              );
                          }
                          if (metData.length !== 0 && notMetData.length !== 0) {
                              return (
                                  <div
                                      style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                      }}
                                  >
                                      <ActTool
                                          data={notMetData}
                                          style={ovalStyleRed}
                                          studyType={params.data?.study_type__title}
                                          studyTypeMethod={params.data?.study_type_method__title}
                                          paramsData={params.data}
                                      />
                                      <ActTool
                                          data={metData}
                                          style={ovalStyleGreen}
                                          studyType={params.data?.study_type__title}
                                          studyTypeMethod={params.data?.study_type_method__title}
                                          paramsData={params.data}
                                      />
                                  </div>
                              );
                          } else if (metData.length !== 0) {
                              return (
                                  <div
                                      style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                      }}
                                  >
                                      <ActTool
                                          data={metData}
                                          style={ovalStyleGreen}
                                          studyType={params.data?.study_type__title}
                                          studyTypeMethod={params.data?.study_type_method__title}
                                          paramsData={params.data}
                                      />
                                  </div>
                              );
                          } else if (notMetData.length !== 0) {
                              return (
                                  <div
                                      style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                      }}
                                  >
                                      <ActTool
                                          data={notMetData}
                                          style={ovalStyleRed}
                                          studyType={params.data?.study_type__title}
                                          studyTypeMethod={params.data?.study_type_method__title}
                                          paramsData={params.data}
                                      />
                                  </div>
                              );
                          } else {
                              return (
                                  <div
                                      style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                      }}
                                  >
                                      <div style={ovalStyleWhite}></div>
                                  </div>
                              );
                          }
                      },
                  },
              ]),
        {
            headerName: "Unilever Product",
            field: "products",
            minWidth: 450,
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            wrapText: true,
            headerComponentParams: {
                template: GetIconHeader("Unilever Product", unileverProduct),
            },
        },
        {
            headerName: "Benchmark Product",
            field: "competitor_products",
            minWidth: 450,
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            wrapText: true,
            headerComponentParams: {
                template: GetIconHeader("Benchmark Product", competitorProduct),
            },
        },
        {
            headerName: "Research Design Type",
            field: "test_design",
            headerComponentParams: {
                template: GetIconHeader("Research Design Type", studyType),
            },
        },
        {
            headerName: "Study Reference",
            field: "study_reference__title",
            headerComponentParams: {
                template: GetIconHeader("Study Reference", studyReference),
            },
        },
        {
            headerName: "Documents",
            field: "share_point_link",
            cellRenderer: linkCellRenderer,
            filter: false,
            headerComponentParams: {
                template: GetIconHeader("Documents", documents),
            },
        },
    ];

    const testCol = [
        {
            headerName: "Id",
            field: "id",
        },
        {
            headerName: "File name",
            field: "title",
            minWidth: 420,
        },
        ...prodCol,
    ];

    const columnDefs = process.env.REACT_APP_SHOW_CORE_DATA_ID === "True" ? testCol : prodCol;

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            suppressMenu: true,
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellStyle: { fontSize: "12px", border: "none", paddingTop: "5px" },
            minWidth: 200,
        };
    }, []);

    const compareArrays = (arr1, arr2) => {
        const arrSorted2 = arr2.slice().sort();
        const isArrEqual =
            arr1.length === arr2.length &&
            arr1
                .slice()
                .sort()
                .every((value, index) => {
                    return value === arrSorted2[index];
                });
        return isArrEqual;
    };

    const onSelectionChanged = useCallback(() => {
        let _selectedRows = gridRef.current.api.getSelectedRows();
        let disabled = true;
        let _country = _selectedRows[0]?.country__title;
        let selectedRowCount = _selectedRows.length;

        if (selectedRowCount !== 0) {
            let matchingRowCount = 0;
            let _deodorantFormat = _selectedRows[0]?.format_data;
            _selectedRows.forEach((each) => {
                const isMatchingRow = (each.country__title === _country && compareArrays(each.format_data, _deodorantFormat)) || isOralCare;
                if (isMatchingRow) {
                    matchingRowCount++;
                }
            });

            if (selectedRowCount === matchingRowCount) {
                disabled = false;
            }
        }

        setSelectedRow(_selectedRows);
        setButtonDisabled(!_selectedRows.length > 0);
        let disableBtn = !_selectedRows.length > 0;
        let tempObj = {
            overallKpi: disableBtn,
            agreementKpi: disableBtn,
            claims: disableBtn,
            correlation: disabled,
            justRight: disableBtn,
        };
        setBtnDisabled(tempObj);

        dispatch(loadSelectedStudiesState(_selectedRows || []));
        dispatch(resetAnalysisResultsStoreState());
        dispatch(loadNavigationAvailabilityState({}));
        dispatch(resetConsumerFilterStoreState());
        dispatch(resetProductsInfoStore());
        dispatch(resetCorrelationStore());
        dispatch(resetClaimsAnalysisStore());
        dispatch(resetRespondentDashboardStoreState());
        dispatch(resetAnalysisKpiStoreState());
        dispatch(resetAnalysisTypeSelectionStoreState());
    }, [isOralCare, dispatch]);

    const getFilterQueryString = () => {
        let queryString = productCategoryId ? `product_category=${productCategoryId}` : "";
        if (Object.keys(studyPageFilters).length > 0 && studyPageFilters && Object.keys(studyPageFilters).length > 0) {
            Object.keys(studyPageFilters).forEach((filter) => {
                let checkedValue = "";
                let _filter = filter;
                studyPageFilters[_filter].forEach((datum) => {
                    if (datum.checked) {
                        if (checkedValue !== "") {
                            checkedValue += ",";
                        }
                        if (_filter === "studyPeriod") {
                            checkedValue += datum.label;
                        } else {
                            checkedValue += datum.value;
                        }
                    }
                });
                if (checkedValue !== "") {
                    if (queryString !== "") {
                        queryString += "&";
                    }

                    if (_filter === "studyType") {
                        _filter = "study_type";
                    } else if (_filter === "studyTypeMethod") {
                        _filter = "study_type_method";
                    } else if (_filter === "deodorantFormat") {
                        _filter = "deodorant_format";
                    } else if (_filter === "studyPeriod") {
                        _filter = "study_period__title";
                    } else if (_filter === "unileverBrand") {
                        _filter = "unilever_brand";
                    } else if (_filter === "country") {
                        _filter = "country";
                    } else if (_filter === "Blind/Branded/Full Mix") {
                        _filter = "blind_branded";
                    } else if (_filter === "CBP") {
                        _filter = "consumer_benefit_platform";
                    } else {
                        _filter = "";
                    }

                    queryString += _filter + "=" + checkedValue;
                }
            });
        }
        return queryString;
    };

    const handleFiltersSubmit = () => {
        const filterQueryString = getFilterQueryString();
        const url = process.env.REACT_APP_SERVER_PATH + `/core-data-models/?archive=false&parsed=true&` + filterQueryString;
        setIsLoading(true);
        try {
            api.get(url)
                .then(async (res) => {
                    const sortedDataByStudyPeriod = (res?.data || []).sort((a, b) => {
                        // Parse the study_period__title to get year and month
                        const [yearA, monthA] = a.study_period__title.split("/").map(Number);
                        const [yearB, monthB] = b.study_period__title.split("/").map(Number);

                        // Create comparable values (higher value for more recent dates)
                        const dateA = new Date(yearA, monthA); // monthA will be 0-indexed
                        const dateB = new Date(yearB, monthB);

                        // Compare dates for sorting in descending order
                        if (dateA !== dateB) {
                            return dateB - dateA; // Sort years in descending order
                        }
                        return dateB - dateA; // For descending order
                    });
                    setRowData(sortedDataByStudyPeriod);
                    dispatch(loadStudyFiltersFlagState(false));
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    };

    const fetchTagsData = useCallback(async () => {
        let url = process.env.REACT_APP_SERVER_PATH + `/claims-tags/all/`;
        if (productCategoryId) {
            url += `?product_category=${productCategoryId}`;
        }
        try {
            await api
                .get(url)
                .then(async (res) => {
                    await dispatch(loadTagsState(res.data));
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [dispatch, productCategoryId]);

    useEffect(() => {
        fetchTagsData();
    }, [fetchTagsData]);

    return (
        <div>
            <Header
                title={headerTitle}
                isStudySelected={!buttonDisabled}
            />
            {isLoading && <Loader />}

            <div
                style={{
                    display: "flex",
                    marginTop: 11,
                    marginBottom: 0,
                    justifyContent: "space-between",
                }}
                className="ms-4 mb-0 me-4"
            >
                <div
                    className=" background-color-white box-shadow--2 d-flex"
                    style={{
                        borderRadius: "5px",
                        minWidth: "calc(100vw - 50px)",
                    }}
                >
                    <StudiesFiltersComponent handleFiltersSubmit={handleFiltersSubmit} />
                    <FiltersHeaderLabel multipleLabel={true} />
                </div>
            </div>
            <FullScreenConatiner>
                <div
                    style={{
                        display: "flex",
                    }}
                >
                    <div
                        style={{
                            minWidth: "calc(100% - 180px)",
                        }}
                        className=" landing-page-table p-2 mb-0"
                    >
                        <div>
                            <SearchBar
                                listedStudies={rowsToListInGrid}
                                allStudies={rowData}
                                selectedRows={selectedRow}
                            />
                        </div>
                        <div
                            className="ag-theme-alpine"
                            style={{
                                height: "calc(100vh - 365px)",
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {isCheckingCompatibility && (
                                <Loader
                                    bgColor=""
                                    color="*000000"
                                />
                            )}
                            <style>
                                {`
                                .ag-header-group-cell-label {
                                    justify-content: center !important;
                                }
                                .ag-header-cell-comp-wrapper {
                                    justify-content: center !important;
                                }
                                `}
                            </style>
                            <AgGridReact
                                rowStyle={{
                                    background: "white",
                                }}
                                ref={gridRef}
                                rowSelection="multiple"
                                rowMultiSelectWithClick={true}
                                defaultColDef={defaultColDef}
                                rowData={rowsToListInGrid}
                                columnDefs={columnDefs}
                                onGridReady={(params) => params.id}
                                onSelectionChanged={onSelectionChanged}
                                // sideBar={true}
                                rowHeight={30}
                                headerHeight={60}
                                floatingFiltersHeight={40}
                                suppressDragLeaveHidesColumns={true}
                                sortingOrder={sortingOrder}
                                onFirstDataRendered={onFirstDataRendered}
                                onBodyScroll={onBodyScroll}
                                onRowDataUpdated={() => selectRows()}
                                suppressRowClickSelection={true}
                            ></AgGridReact>
                        </div>
                        <div className="d-flex mt-1">
                            <span
                                className="text-xxs"
                                style={{
                                    color: "#495057",
                                    marginRight: 20,
                                    fontWeight: 600,
                                }}
                            >
                                Selected rows: {selectedRow.length}
                            </span>
                            <span
                                className="text-xxs"
                                style={{
                                    color: "#495057",
                                    fontWeight: 600,
                                }}
                            >
                                Total rows: {rowsToListInGrid.length}
                            </span>
                            <span
                                className="text-xxs"
                                style={{
                                    color: "#495057",
                                    fontWeight: 600,
                                    marginLeft: "auto",
                                }}
                            >
                                *NOTE: Select studies of same country and format to perform correlation
                            </span>
                        </div>
                        {!(isSkinCare && role?.toLowerCase() === "user") && (
                            <div
                                className="d-flex"
                                style={{
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    width: "100%",
                                    marginBottom: "5px",
                                    height: "52px",
                                }}
                            >
                                <div>
                                    {textThemeAnalysisNote && (
                                        <span
                                            className="text-xxs"
                                            style={{
                                                color: "#495057",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {textThemeAnalysisNote}
                                        </span>
                                    )}
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            margin: "5px 0px 5px 20px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                ...ovalStyleGreen,
                                                width: "12px",
                                                height: "12px",
                                            }}
                                        />
                                        <span
                                            className="text-xxs"
                                            style={{
                                                color: "#495057",
                                                fontWeight: 600,
                                                marginLeft: "auto",
                                            }}
                                        >
                                            Action Standard Met
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            margin: "5px 0px 5px 20px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                ...ovalStyleRed,
                                                width: "12px",
                                                height: "12px",
                                            }}
                                        />
                                        <span
                                            className="text-xxs"
                                            style={{
                                                color: "#495057",
                                                fontWeight: 600,
                                                marginLeft: "auto",
                                            }}
                                        >
                                            Action Standard Not Met
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            margin: "5px 0px 5px 20px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                ...ovalStyleWhite,
                                                width: "12px",
                                                height: "12px",
                                            }}
                                        />
                                        <span
                                            className="text-xxs"
                                            style={{
                                                color: "#495057",
                                                fontWeight: 600,
                                                marginLeft: "auto",
                                            }}
                                        >
                                            Action Standard information not entered
                                        </span>
                                    </div>
                                    {isBeautyAndWellbeing && (
                                        <div
                                            style={{
                                                margin: "5px 0px 5px 20px",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    ...ovalStyleYellow,
                                                    width: "12px",
                                                    height: "12px",
                                                }}
                                            />
                                            <span
                                                className="text-xxs"
                                                style={{
                                                    color: "#495057",
                                                    fontWeight: 600,
                                                    marginLeft: "auto",
                                                }}
                                            >
                                                Action Standard Parity
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: "10px",
                            }}
                        >
                            {isSkinCare && role?.toLowerCase() === "user" && (
                                <Button
                                    size="small"
                                    style={{
                                        margin: "0px 0px 0px 10px",
                                        fontSize: "0.85rem",
                                    }}
                                    className={selectedStudies.length === 1 ? "custom-btn liner-light-blue-background color-white" : "custom-btn custom-btn-action-standard"}
                                    variant={selectedStudies.length === 1 ? "contained" : "outlined"}
                                    disabled={selectedStudies.length !== 1}
                                    onClick={() => setActionStandardModal(true)}
                                >
                                    Action Standards
                                </Button>
                            )}
                            <Button
                                size="small"
                                className={"custom-btn custom-btn-outlined color-blue"}
                                variant="outlined"
                                onClick={() => navigate("/driver-analysis")}
                                style={{
                                    margin: "0px 0px 0px 10px",
                                    background: "#fff",
                                    fontSize: "0.85rem",
                                }}
                            >
                                Show latest driver analysis report
                            </Button>
                        </div>

                        {isSkinCare && actionStandardModal && selectedStudies.length === 1 && (
                            <Dialog
                                maxWidth="xl"
                                fullWidth={true}
                                open={actionStandardModal}
                            >
                                <DialogTitle
                                    style={{
                                        backgroundColor: "rgb(51, 94, 234,0.1)",
                                        color: "rgb(51, 94, 234)",
                                        fontSize: 15,
                                        textAlign: "center",
                                        padding: "10px 12px",
                                    }}
                                >
                                    Action Standards
                                    <IconButton
                                        aria-label="close"
                                        sx={{ position: "absolute", right: 8, top: 5, color: "rgb(51, 94, 234) !important" }}
                                        size="small"
                                        onClick={() => closeActionStandardModal("close")}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent style={{ fontSize: 12 }}>
                                    <ActionStandardTabs
                                        coreDataId={selectedStudies[0]?.id}
                                        weighted={selectedStudies[0]?.weighted}
                                        studyTypeMethod={selectedStudies[0]?.study_type_method}
                                        closeActionStandardModal={closeActionStandardModal}
                                        coreData={selectedStudies[0]}
                                        viewOnly={true}
                                    />
                                </DialogContent>
                            </Dialog>
                        )}
                        <Snackbar
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                            open={snackBarOpen}
                            autoHideDuration={6000}
                            onClose={handleSnackBarClose}
                        >
                            <MuiAlert
                                elevation={6}
                                variant="filled"
                                onClose={handleSnackBarClose}
                                severity={snackInfo[0]}
                                sx={{ maxWidth: 700 }}
                            >
                                {snackInfo[1]}
                            </MuiAlert>
                        </Snackbar>

                        <div
                            style={{
                                position: "relative",
                                top: isSkinCare && role?.toLowerCase() === "user" ? "52px" : "30px",
                            }}
                        >
                            <MainNavigation
                                buttons={btnArr}
                                btnDisabledObj={btnDisabled}
                                navigationLoader={navigationLoader}
                                isFragrance={isFragrance}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            padding: "10px 0px",
                            borderRadius: "5px",
                            backgroundColor: "#EDEBFF",
                        }}
                    >
                        <div
                            style={{
                                width: 180,
                                height: "calc(100vh - 136px)",
                                overflowY: "auto",
                                overflowX: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}
                        >
                            <StudySelectionSummary />
                        </div>
                    </div>
                </div>
            </FullScreenConatiner>
        </div>
    );
}

// Typechecking props for the Study
Study.propTypes = {
    headerTitle: PropTypes.string,
};

export default Study;
